'use client'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { BodyText } from '@/components/ui/typography'
import {
  getSessionCards,
  type SessionCard,
  type SessionCardsResult
} from '@/lib/fsrs/fetch-session'
import { invalidateCardQueries } from '@/lib/fsrs/invalidate-queries'
import { CollectionEntity } from '@/lib/fsrs/types'
import { getCuratedSlug } from '@/types/collections'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import FlashcardRunner from './flashcard-runner'

type Props = {
  collectionId: string // Collection ID (UUID for user collections, slug for curated, or type for virtual)
  collection?: CollectionEntity | null
  curatedSlug?: string | null // For curated collections
  initialCards?: SessionCard[]
  timeZone?: string
  userId: string
  onMetaChange?: (meta: {
    remaining: number
    total: number
    newAllowed?: number
    newLimit?: number
    reviewCount?: number
  }) => void
  onExit?: () => void
  onRestart?: () => void
}

export function FlashcardSession({
  collectionId,
  collection,
  curatedSlug,
  initialCards,
  timeZone,
  userId,
  onMetaChange,
  onExit,
  onRestart
}: Props) {
  const queryClient = useQueryClient()
  const actualCuratedSlug =
    curatedSlug ?? (collectionId ? getCuratedSlug(collectionId) : null)
  const [cards, setCards] = useState<SessionCard[]>(initialCards || [])
  const [stats, setStats] = useState<Partial<SessionCardsResult> | null>(null)
  const [loading, setLoading] = useState(!initialCards?.length)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)
  const [remaining, setRemaining] = useState(initialCards?.length ?? 0)
  const total = cards.length

  // Guard to prevent duplicate concurrent calls (React StrictMode protection)
  const isLoadingRef = useRef(false)

  const canShowRunner = cards.length > 0 && !loading && !error

  const loadSession = useCallback(async () => {
    if (!collectionId || !userId) return
    // Prevent duplicate concurrent calls
    if (isLoadingRef.current) return
    isLoadingRef.current = true

    setLoading(true)
    setError(null)
    setIsComplete(false)
    try {
      const result = await getSessionCards(collectionId, userId, {
        collection,
        curatedSlug: actualCuratedSlug,
        timeZone
      })
      setCards(result.cards)
      setStats(result)
      setRemaining(result.cards.length)

      // Invalidate all card-related queries since new cards may have been created via JIT
      // This ensures tables and counts are updated when user exits study mode
      invalidateCardQueries(queryClient, userId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session')
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }, [
    collection,
    collectionId,
    actualCuratedSlug,
    timeZone,
    userId,
    queryClient
  ])

  useEffect(() => {
    if (initialCards?.length) return
    loadSession()
  }, [loadSession, initialCards])

  const handleComplete = () => {
    setIsComplete(true)
  }

  useEffect(() => {
    if (onMetaChange) {
      onMetaChange({
        remaining,
        total,
        newAllowed: stats?.newAllowed,
        newLimit: stats?.newLimit,
        reviewCount: stats?.reviews?.length
      })
    }
  }, [
    onMetaChange,
    remaining,
    stats?.newAllowed,
    stats?.newLimit,
    stats?.reviews?.length,
    total
  ])

  return (
    <div className="flex w-full flex-col items-center gap-6">
      {loading && (
        <div className="flex w-full max-w-xl flex-col items-center gap-6">
          <div className="bg-card relative flex w-full flex-col items-center justify-center rounded-lg p-6 shadow-sm">
            <div className="flex min-h-24 flex-col items-center justify-center py-4">
              <Skeleton className="h-12 w-64" />
            </div>
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
      )}

      {error && (
        <div className="text-destructive flex flex-col items-center gap-2">
          <p>{error}</p>
          <Button variant="outline" size="sm" onClick={() => loadSession()}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && cards.length === 0 && !isComplete && (
        <div className="text-muted-foreground flex flex-col items-center gap-3 text-sm">
          <BodyText className="text-foreground text-center text-base">
            You’re all caught up.
          </BodyText>
          <p className="text-muted-foreground text-center text-xs">
            No cards are due right now.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => loadSession()}>
              Refresh
            </Button>
            {onExit ? (
              <Button variant="ghost" size="sm" onClick={onExit}>
                Exit
              </Button>
            ) : null}
          </div>
        </div>
      )}

      {isComplete && !loading && !error && (
        <div className="flex flex-col items-center gap-3">
          <BodyText className="text-center text-base">
            Session complete!
          </BodyText>
          <p className="text-muted-foreground text-center text-xs">
            You’ve reviewed everything due in this collection.
          </p>
          <div className="flex gap-2">
            {onRestart ? (
              <Button variant="default" size="sm" onClick={onRestart}>
                Restart
              </Button>
            ) : null}
            {onExit ? (
              <Button variant="ghost" size="sm" onClick={onExit}>
                Exit
              </Button>
            ) : null}
          </div>
        </div>
      )}

      {canShowRunner && (
        <FlashcardRunner
          key={cards.map((c) => c.id).join('-')}
          initialCards={cards}
          collection={collection}
          collectionId={collectionId}
          curatedSlug={actualCuratedSlug}
          onComplete={handleComplete}
          onQueueChange={({ remaining: r }) => setRemaining(r)}
        />
      )}
    </div>
  )
}

export default FlashcardSession
